import { Injectable } from '@angular/core';
import { ApiService } from '../shared/api.service';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';

// Interfaces
export interface NotificationConfiguration {
  id?: number;
  name: string;
  title: string;
  message_template: string;
  push_enabled: boolean;
  whatsapp_enabled: boolean;
  whatsapp_group_number?: string;
  target_roles: number[];
  conditions?: any;
  service_statuses?: number[];
  maintenance_statuses?: number[];
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface NotificationLog {
  id: number;
  notification_configuration_id: number;
  user_id: number;
  type: string;
  status: string;
  title: string;
  message: string;
  recipient_phone?: string;
  metadata?: any;
  error_message?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email?: string;
  };
  notification_configuration?: {
    id: number;
    name: string;
    title: string;
  };
}

export interface WhatsAppConfiguration {
  id?: number;
  provider: string;
  api_url: string;
  api_key: string;
  api_secret?: string;
  phone_number_id?: string;
  from_number: string;
  additional_config?: any;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface ToastNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications: ToastNotification[] = [];
  private notificationHistory = new Set<string>();
  private readonly HISTORY_WINDOW_MS = 300000; // 5 minutes
  private notificationsSubject = new BehaviorSubject<ToastNotification[]>([]);

  constructor(private apiService: ApiService) {}

  // Notification Configurations
  getNotificationConfigurations(): Observable<any> {
    return this.apiService.get('notification-configurations');
  }

  createNotificationConfiguration(data: NotificationConfiguration): Observable<any> {
    return this.apiService.post('notification-configurations', data);
  }

  updateNotificationConfiguration(id: number, data: NotificationConfiguration): Observable<any> {
    return this.apiService.put(`notification-configurations/${id}`, data);
  }

  deleteNotificationConfiguration(id: number): Observable<any> {
    return this.apiService.delete(`notification-configurations/${id}`);
  }

  toggleNotificationConfiguration(id: number): Observable<any> {
    return this.apiService.post(`notification-configurations/${id}/toggle`, {});
  }

  getNotificationRoles(): Observable<any> {
    return this.apiService.get('notification-roles');
  }

  getNotificationTemplates(): Observable<any> {
    return this.apiService.get('notification-templates');
  }

  getServiceStatuses(): Observable<any> {
    return this.apiService.get('notification-service-statuses');
  }

  getMaintenanceStatuses(): Observable<any> {
    return this.apiService.get('notification-maintenance-statuses');
  }

  // Notification Logs
  getNotificationLogs(params: any = {}): Observable<any> {
    const queryString = new URLSearchParams(params).toString();
    return this.apiService.get(`notification-logs?${queryString}`);
  }

  getNotificationStatistics(params: any = {}): Observable<any> {
    const queryString = new URLSearchParams(params).toString();
    return this.apiService.get(`notification-statistics?${queryString}`);
  }

  retryFailedNotifications(): Observable<any> {
    return this.apiService.post('notification-logs/retry-failed', {});
  }

  cleanupNotificationLogs(days: number): Observable<any> {
    return this.apiService.post('notification-logs/cleanup', { days });
  }

  // WhatsApp Configurations
  getWhatsAppConfigurations(): Observable<any> {
    return this.apiService.get('whatsapp-configurations');
  }

  createWhatsAppConfiguration(data: WhatsAppConfiguration): Observable<any> {
    return this.apiService.post('whatsapp-configurations', data);
  }

  updateWhatsAppConfiguration(id: number, data: WhatsAppConfiguration): Observable<any> {
    return this.apiService.put(`whatsapp-configurations/${id}`, data);
  }

  deleteWhatsAppConfiguration(id: number): Observable<any> {
    return this.apiService.delete(`whatsapp-configurations/${id}`);
  }

  testWhatsAppConfiguration(id: number): Observable<any> {
    return this.apiService.post(`whatsapp-configurations/${id}/test`, {});
  }

  getWhatsAppProviders(): Observable<any> {
    return this.apiService.get('whatsapp-providers');
  }

  sendTestWhatsAppMessage(phone: string, message: string): Observable<any> {
    return this.apiService.post('whatsapp/send-test', { phone, message });
  }

  // Browser Notifications
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }

  showBrowserNotification(title: string, message: string, options: any = {}): void {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: message,
        icon: options.icon || '/assets/icons/notification-icon.png',
        badge: options.badge || '/assets/icons/badge-icon.png',
        tag: options.tag || 'logist-notification',
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false
      });

      // Auto close after 5 seconds if not requiring interaction
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };
    }
  }

  // Toast Notification Management
  addNotification(notification: ToastNotification): void {
    this.notifications.unshift(notification);
    
    // Keep only last 10 notifications
    if (this.notifications.length > 10) {
      this.notifications = this.notifications.slice(0, 10);
    }
    
    // Emit changes to subscribers
    this.notificationsSubject.next([...this.notifications]);
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    // Emit changes to subscribers
    this.notificationsSubject.next([...this.notifications]);
  }

  getNotifications(): Observable<ToastNotification[]> {
    return this.notificationsSubject.asObservable();
  }

  getNotificationsArray(): ToastNotification[] {
    return this.notifications;
  }

  clearAllNotifications(): void {
    this.notifications = [];
    // Emit changes to subscribers
    this.notificationsSubject.next([]);
  }

  // Duplicate Detection
  isNotificationAlreadyShown(title: string, message: string): boolean {
    const key = this.createHistoryKey(title, message);
    return this.notificationHistory.has(key);
  }

  markNotificationAsShown(title: string, message: string): void {
    const key = this.createHistoryKey(title, message);
    this.notificationHistory.add(key);
    
    // Clean up old entries after history window
    setTimeout(() => {
      this.notificationHistory.delete(key);
    }, this.HISTORY_WINDOW_MS);
  }

  clearGlobalNotificationHistory(): void {
    this.notificationHistory.clear();
  }

  private createHistoryKey(title: string, message: string): string {
    const timeWindow = Math.floor(Date.now() / this.HISTORY_WINDOW_MS);
    return `${title}-${message}-${timeWindow}`;
  }
}
